/**
 * consoleBuffer.js — Capture `console.error` and `console.warn` calls into ring
 * buffers so they can be attached to bug reports.
 *
 * Monkey-patches `console.error` / `console.warn` once on first import. The
 * original behaviour is preserved — nothing is suppressed, the dev console
 * still shows errors and warnings as usual. Warnings (React key warnings,
 * deprecations, act() warnings) frequently point straight at the bug, so we
 * keep them in a separate buffer from hard errors.
 */

const MAX_ENTRIES = 20;
let _errorBuffer = [];
let _warnBuffer = [];
let _patched = false;

function _format(args) {
  try {
    return args
      .map((a) => {
        if (a instanceof Error) {
          return `${a.name}: ${a.message}\n${a.stack || ''}`;
        }
        if (typeof a === 'string') return a;
        try { return JSON.stringify(a); } catch { return String(a); }
      })
      .join(' ');
  } catch {
    return '[unserializable console payload]';
  }
}

function _push(buffer, args) {
  try {
    buffer.push({
      ts: new Date().toISOString(),
      message: _format(args),
    });
    if (buffer.length > MAX_ENTRIES) {
      buffer.splice(0, buffer.length - MAX_ENTRIES);
    }
  } catch { /* never let our capture break console */ }
}

/** Install the patch (idempotent). Safe to call multiple times. */
export function installConsoleCapture() {
  if (_patched || typeof window === 'undefined') return;

  const originalError = console.error.bind(console);
  console.error = (...args) => {
    _push(_errorBuffer, args);
    return originalError(...args);
  };

  const originalWarn = console.warn.bind(console);
  console.warn = (...args) => {
    _push(_warnBuffer, args);
    return originalWarn(...args);
  };

  _patched = true;
}

/** Return a *copy* of buffered console.error entries (oldest first). */
export function getConsoleErrors() {
  return _errorBuffer.slice();
}

/** Return a *copy* of buffered console.warn entries (oldest first). */
export function getConsoleWarnings() {
  return _warnBuffer.slice();
}

export function clearConsoleErrors() {
  _errorBuffer = [];
}

export function clearConsoleWarnings() {
  _warnBuffer = [];
}
